'use client';

import { usePathname } from 'next/navigation';

/**
 * Returns true if the current pathname matches the given href.
 * Uses prefix matching: `/events/123` matches `/events`.
 * Exact match for `/` to avoid matching everything.
 */
export function useActiveLink(href: string): boolean {
  const pathname = usePathname();

  if (href === '/') {
    return pathname === '/';
  }

  return pathname === href || pathname.startsWith(href + '/');
}

/**
 * Discover is reachable on two surfaces with different path shapes:
 *  - marketing-city (e.g. `clt.loci.city/discover`) → pathname `/discover`
 *  - app            (e.g. `app.loci.city/clt/discover`) → pathname `/<city>/discover`
 *
 * Both should mark the header's "Discover" link as the current page, so the
 * matcher accepts either the bare `/discover` path or `/<segment>/discover`
 * with an optional trailing path. The leading-segment match deliberately uses
 * `[^/]+` rather than the registered `CityCode` set to keep this hook
 * city-list-agnostic — adding a new edition shouldn't require touching the
 * header's active-state logic.
 */
const DISCOVER_PATH_PATTERN = /^\/[^/]+\/discover(?:\/.*)?$/;

export function useDiscoverActive(): boolean {
  const pathname = usePathname();
  if (pathname === '/discover' || pathname.startsWith('/discover/')) return true;
  return DISCOVER_PATH_PATTERN.test(pathname);
}
